import React, { useId, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Checkbox,
    IconButton,
    ListItemText,
    Menu,
    MenuItem,
    Tooltip,
} from '@mui/material';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';

import { compactIconButtonSx } from '../pos_sizing_table/tableStyles';

// An icon menu choosing which groups the chart shows; the tooltip reports how
// many are shown.
export default function PlotGroupSelector({
    groupNames,
    includedGroups,
    onChange,
    groupLabelPlural,
}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const menuId = useId();
    const open = Boolean(anchorEl);
    const selectionLabel = `Choose ${groupLabelPlural.toLowerCase()} to visualize`;
    const toggle = (category) => {
        const next = includedGroups.includes(category)
            ? includedGroups.filter((name) => name !== category)
            : [...includedGroups, category];
        onChange({ target: { value: next } });
    };

    return (
        <>
            <Tooltip
                title={`${selectionLabel} (${includedGroups.length} of ${groupNames.length} shown)`}
            >
                <span>
                    <IconButton
                        size="small"
                        onClick={(event) => setAnchorEl(event.currentTarget)}
                        disabled={groupNames.length === 0}
                        aria-label={selectionLabel}
                        aria-controls={open ? menuId : undefined}
                        aria-expanded={open ? 'true' : undefined}
                        aria-haspopup="menu"
                        sx={compactIconButtonSx}
                    >
                        <FilterListRoundedIcon />
                    </IconButton>
                </span>
            </Tooltip>
            <Menu
                id={menuId}
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                slotProps={{ paper: { style: { maxHeight: 300, width: 250 } } }}
            >
                {groupNames.map((category) => {
                    const checked = includedGroups.includes(category);
                    return (
                        <MenuItem
                            key={category}
                            dense
                            role="menuitemcheckbox"
                            aria-checked={checked}
                            onClick={() => toggle(category)}
                        >
                            <Checkbox size="small" checked={checked} tabIndex={-1} sx={{ p: 0.5, mr: 0.5 }} />
                            <ListItemText primary={category} />
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
}

PlotGroupSelector.propTypes = {
    groupNames:     PropTypes.arrayOf(PropTypes.string).isRequired,
    includedGroups: PropTypes.arrayOf(PropTypes.string).isRequired,
    onChange:        PropTypes.func.isRequired,
    groupLabelPlural: PropTypes.string.isRequired,
};
